import numeral from 'numeral';

/**
 * Format token amounts with simplified rules using numeral.js
 */
export function formatTokenAmount(amount: string): string {
  const numAmount = Number.parseFloat(amount);

  if (isNaN(numAmount) || numAmount === 0) {
    return '0';
  }

  const absAmount = Math.abs(numAmount);

  // Large numbers (1M+) with abbreviations
  if (absAmount >= 1_000_000) {
    return numeral(numAmount).format('0.00a');
  }

  // Numbers with thousands separators (1K+)
  if (absAmount >= 1000) {
    return numeral(numAmount).format('0,0.[0000]');
  }

  // Very small numbers (< 0.0001)
  if (absAmount < 0.0001 && absAmount > 0) {
    return numeral(numAmount).format('0.[000000000]');
  }

  // Small numbers (< 1)
  if (absAmount < 1) {
    return numeral(numAmount).format('0.[000000]');
  }

  // Default case
  return numeral(numAmount).format('0.[0000]');
}

/**
 * Calculate USD value from token amount and price
 */
export function calculateUsdValue(
  amount: string,
  pricePerToken: number,
): number {
  const numAmount = Number.parseFloat(amount);
  if (Number.isNaN(numAmount) || Number.isNaN(pricePerToken)) {
    return 0;
  }

  const usdValue = numAmount * pricePerToken;

  // Handle very small USD values
  if (Math.abs(usdValue) < 0.000001) {
    return 0;
  }

  return Number.parseFloat(usdValue.toFixed(8));
}

/**
 * Format USD values for display using numeral.js
 */
export function formatUsdValue(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  if (value === 0) {
    return '$0.00';
  }

  const absValue = Math.abs(value);

  // Large values (1M+) with abbreviations
  if (absValue >= 1_000_000) {
    return `$${numeral(value).format('0.00a')}`;
  }

  // Values with thousands separators (1K+)
  if (absValue >= 1000) {
    return `$${numeral(value).format('0,0.00')}`;
  }

  // Very small values
  if (absValue < 0.000001) {
    return '<$0.000001';
  }

  // Small values (< $0.01)
  if (absValue < 0.01) {
    return `$${numeral(value).format('0.[000000]')}`;
  }

  // Small values (< $1)
  if (absValue < 1) {
    return `$${numeral(value).format('0.[0000]')}`;
  }

  // Default case
  return `$${numeral(value).format('0.00')}`;
}

/**
 * Format percentages using numeral.js
 */
export function formatPercentage(value: number): string {
  if (isNaN(value)) {
    return 'N/A';
  }

  const absValue = Math.abs(value);

  // Very large percentages
  if (absValue >= 1000) {
    return numeral(value / 100).format('0,0.0%');
  }

  // Default percentage formatting
  return numeral(value / 100).format('0.00%');
}

/**
 * Format numbers with configurable options using numeral.js
 */
export function formatNumber(
  value: number,
  options: {
    decimals?: number;
    abbreviate?: boolean;
    useThousandsSeparator?: boolean;
  } = {},
): string {
  const {
    decimals = 2,
    abbreviate = false,
    useThousandsSeparator = true,
  } = options;

  if (isNaN(value)) {
    return 'N/A';
  }

  if (value === 0) {
    return '0';
  }

  const absValue = Math.abs(value);

  // Abbreviate large numbers if requested
  if (abbreviate && absValue >= 1_000_000) {
    return numeral(value).format('0.00a');
  }

  // Build decimal format once
  const decimalFormat =
    decimals > 0 ? `.[${Array(decimals).fill('0').join('')}]` : '';

  // Use thousands separator if requested and value >= 1000
  if (useThousandsSeparator && absValue >= 1000) {
    return numeral(value).format(`0,0${decimalFormat}`);
  }

  // Default formatting
  return numeral(value).format(`0${decimalFormat}`);
}
